"use client";

import React from "react";
import { CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Wrench, FlaskConical, GraduationCap, FileSpreadsheet, Clock } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface ServiceItem {
  id: string;
  category: string;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  features: string[];
  ctaText: string;
  href: string;
}
const servicesData: ServiceItem[] = [{
  id: "qualification",
  category: "Regulatory Compliance",
  title: "Equipment Qualification (IQ / OQ / PQ)",
  description: "Complete validation protocols tailored to FDA, GMP, and ISO compliance standards across regulated testing facilities.",
  icon: ShieldCheck,
  features: ["Custom installation & operational verification", "Traceable calibration documentation", "GAMP 5 aligned validation plans"],
  ctaText: "Explore Qualification",
  href: "/ServicesPage"
}, {
  id: "maintenance",
  category: "Lifecycle Support",
  title: "Preventative Maintenance & Servicing",
  description: "Scheduled servicing contracts, priority emergency repairs, and certified OEM spare parts for optimal instrument uptime.",
  icon: Wrench,
  features: ["Annual planned maintenance schedules", "Rapid on-site engineer dispatch across Ireland & UK", "Comprehensive diagnostic checklists"],
  ctaText: "Service Contracts",
  href: "/ServicesPage"
}, {
  id: "application",
  category: "Laboratory Consulting",
  title: "Method Development & Application",
  description: "Specialized assistance for chromatographic, spectroscopic, and powder characterization method optimization.",
  icon: FlaskConical,
  features: ["Workflow & sample preparation audits", "Solvent recovery & throughput analysis", "Direct technical liaison with instrument specialists"],
  ctaText: "Application Support",
  href: "/ServicesPage"
}, {
  id: "training",
  category: "Workforce Enablement",
  title: "Operator Training & Technical Workshops",
  description: "Hands-on instruction and accredited operational workshops to maintain laboratory precision and safety standards.",
  icon: GraduationCap,
  features: ["On-site and remote tailored curricula", "Certificate of competence for laboratory staff", "Troubleshooting & best practice guidelines"],
  ctaText: "Book Training",
  href: "/ContactPage"
}];
const serviceMetrics = [{
  title: "Rapid Response Time",
  description: "Dedicated technical and engineer support",
  icon: Clock
}, {
  title: "Fully Traceable Reports",
  description: "Audit-ready calibration and validation data",
  icon: FileSpreadsheet
}, {
  title: "Validated Installation Process",
  description: "Comprehensive IQ / OQ / PQ protocols",
  icon: ShieldCheck
}];
export default function Services() {
  return <section id="companyservices" data-nav="light" className="bg-background py-16 sm:py-24 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 lg:mb-16">
          <div className="max-w-2xl">
            <Badge variant="secondary" className="mb-3 text-primary font-medium tracking-wide uppercase">
              Technical & Lifecycle Services
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground font-default">
              Complete Analytical & Laboratory Support
            </h2>
            <p className="mt-4 text-muted-foreground text-base sm:text-lg leading-relaxed">
              We provide application-led maintenance, qualification, and operational guidance across regulated manufacturing and analytical labs in Ireland and the UK.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" asChild>
              <Link to="/ServicesPage">
                View All Services
              </Link>
            </Button>
            <Button asChild>
              <Link to="/ContactPage">
                Request Service Quote
              </Link>
            </Button>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {servicesData.map((service, index) => {
          const IconComponent = service.icon;
          return <Card key={service.id} data-index={index} className="group relative flex flex-col justify-between border-border hover:border-primary/40 transition-colors shadow-xs hover:shadow-md bg-card">
                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <div className="p-3 rounded-lg bg-secondary text-primary">
                        <IconComponent className="size-6" />
                      </div>
                      <span className="text-xs font-medium tracking-wide uppercase text-muted-foreground bg-muted/60 px-2.5 py-1 rounded-md">
                        {service.category}
                      </span>
                    </div>
                    <CardTitle className="text-xl sm:text-2xl text-card-foreground font-semibold font-default group-hover:text-primary transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm sm:text-base mt-2">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2">
                    <ul className="space-y-2.5">
                      {service.features.map((feature, fIndex) => <li key={fIndex} data-index={fIndex} className="flex items-start gap-2.5 text-sm text-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </div>

                <CardFooter className="pt-6 border-t border-border mt-6">
                  <Button variant="ghost" className="w-full justify-between px-2 text-foreground group-hover:text-primary font-medium" asChild>
                    <Link to={service.href}>
                      <span>{service.ctaText}</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>;
        })}
        </div>

        {/* Quality Assurance Strip */}
        <div className="mt-12 lg:mt-16 rounded-xl bg-secondary p-6 sm:p-8 border border-border">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 divide-y sm:divide-y-0 sm:divide-x divide-border">
            {serviceMetrics.map((metric, index) => {
            const MetricIcon = metric.icon;
            return <div key={metric.title} data-index={index} className="flex items-center gap-4 pt-4 sm:pt-0 sm:px-6 first:pt-0 first:px-0">
                  <div className="p-2.5 rounded-md bg-background text-primary shadow-2xs">
                    <MetricIcon className="size-5" />
                  </div>
                  <div>
                    <div className="text-base sm:text-lg font-semibold text-secondary-foreground font-default">
                      {metric.title}
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground">
                      {metric.description}
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}